import React, { useEffect, useRef, useState } from 'react';
import {
  DropdownMenuPropsModel,
  isNestedMenu,
} from './dropdown-menu-props.model';
import { usePopper } from 'react-popper';
import TooltipPortal from './Tooltip-portal';
import NestedMenu from './Nested-menu';
import { slugify } from '../../../helpers/functions';
import { Icon } from '../../UI';

export const DropdownMenu = ({
  dropdownItems,
  arrowPosition,
  buttonClassName,
  children,
  disableCursorOverride,
  dropdownArrowClassName = '',
  dropdownClassName = '',
  dropdownIconClassName = '',
  dropdownItemsComponents,
  dropdownItemsData,
  dropdownTooltipClassName = 'border border-grey-10 shadow-2xl bg-white',
  dropdownTooltipXOffset,
  dropdownTooltipYOffset,
  hideOnItemClick,
  hideOnOutsideClick,
  hideOnMouse,
  isDropdownDisabled,
  isDropdownVisible,
  setDropdownAsStatic,
  showArrow,
  handleClick,
  placement,
  isNested = false,
  tooltipChildren = { element: null, placement: 'top' },
  onClose,
  onOpen,
}: DropdownMenuPropsModel) => {
  const popperRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isVisible, setIsVisible] = useState(false);
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );

  const hasNestedMenu = dropdownItems.some((el) => isNestedMenu(el));

  useEffect(() => {
    setPopperVisibility(isDropdownVisible || false);
  }, [isDropdownVisible]);

  useEffect(() => {
    if (isDropdownDisabled) {
      setPopperVisibility(false);
    }
  }, [isDropdownDisabled]);

  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      placement,
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            altAxis: true,
            padding: 16,
          },
        },
        {
          name: 'offset',
          options: {
            offset: [dropdownTooltipXOffset || 0, dropdownTooltipYOffset || 16],
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['right-start', 'bottom'],
          },
        },
      ],
    }
  );

  // Hide dropdown if user click on another page element
  const handleWindowClick = (e) => {
    if (popperRef.current && !popperRef.current.contains(e.target)) {
      if (hideOnOutsideClick) {
        setPopperVisibility(false);
      }
    }
  };

  // Listen click on page
  useEffect(() => {
    window.addEventListener('mousedown', handleWindowClick);

    return () => {
      window.removeEventListener('mousedown', handleWindowClick);
    };
  }, []);

  // Show/Hide dropdown
  const setPopperVisibility = (visibility) => {
    if (update) {
      if (visibility) {
        onOpen?.();
      } else {
        onClose?.();
      }
      setIsVisible(visibility);
      update();
    }
  };

  // Handle click on item inside dropdown
  const handleClickOnSelect = (item: string, data: any, key?: string) => {
    if (hideOnItemClick) {
      setPopperVisibility(false);
    }

    if (handleClick) {
      handleClick(item, data, key || slugify(item));
    }
  };

  const DropdownArrow = () => (
    <>
      {showArrow && (
        <Icon
          className={`${dropdownArrowClassName} cursor-pointer text-black`}
          svgName={isVisible ? 'arrowUp' : 'arrowDown'}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </>
  );

  function onMouse() {
    if (!isDropdownDisabled) {
      setPopperVisibility((prev) => {
        if (prev && !hideOnMouse) {
          return prev;
        }

        return !prev;
      });
    }
  }

  return (
    <div
      ref={popperRef}
      className={`
        ${dropdownClassName}
        ${isVisible && 'z-70'}
        ${isNested && 'w-full'}
      `}
    >
      <div
        className={`
          ${dropdownIconClassName}
          justify-center
          relative
          flex
          items-center
          gap-1
          select-none
          ${disableCursorOverride ? 'pointer-events-none' : 'cursor-pointer'}
        `}
        ref={setReferenceElement}
        {...(isNested ? { onMouseEnter: onMouse } : { onMouseDown: onMouse })}
        onMouseLeave={() => {
          if (isNested) {
            timeoutRef.current = setTimeout(() => {
              setPopperVisibility(false);
            }, 70);
          }
        }}
      >
        {arrowPosition === 'left' && <DropdownArrow />}
        {children}
        {(!arrowPosition || arrowPosition === 'right') && <DropdownArrow />}
      </div>

      <TooltipPortal hasNestedMenu={hasNestedMenu || isNested}>
        <div
          id="tooltip"
          className={`
            ${dropdownTooltipClassName}
            relative
            z-100
            rounded-xl
            text-white
            dark:bg-grey-90
            dark:border-grey-70
            ${isVisible ? 'block' : 'hidden'}
            overflow-y-auto
          `}
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
          onMouseEnter={() => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
          }}
          onMouseLeave={() => setPopperVisibility(false)}
        >
          {tooltipChildren.element &&
            tooltipChildren.placement === 'top' &&
            tooltipChildren.element}
          {setDropdownAsStatic
            ? dropdownItemsComponents && dropdownItemsComponents[0]
            : dropdownItems.map((item, index) => {
                let element = dropdownItemsComponents
                  ? dropdownItemsComponents[index]
                  : item;
                let isNestedItem = false;
                if (isNestedMenu(item)) {
                  isNestedItem = true;
                  element = (
                    <NestedMenu
                      item={item.item}
                      dropdownItems={item.dropdownItems}
                      dropdownTooltipClassName={dropdownTooltipClassName}
                      dropdownItemsData={item.dropdownItemsData}
                      handleClick={item.handleClick || handleClick}
                      tooltipChildren={item.tooltipChildren}
                      hideItemOnClick={item.hideItemOnClick || hideOnItemClick}
                    />
                  );
                }

                let withBorder = false;
                if (
                  typeof element === 'object' &&
                  !React.isValidElement(element)
                ) {
                  if (element.withBorder) {
                    withBorder = true;
                  }

                  element = element.item;
                }

                return (
                  <React.Fragment key={element + index}>
                    <div
                      {...(isNestedItem
                        ? {}
                        : {
                            onClick: (e) => {
                              e.stopPropagation();
                              if (dropdownItemsData) {
                                handleClickOnSelect(
                                  typeof element === 'string'
                                    ? (element as string)
                                    : typeof dropdownItems[index] === 'string'
                                    ? (dropdownItems[index] as string)
                                    : '',
                                  dropdownItemsData[index],
                                  element.key
                                );
                              } else {
                                handleClickOnSelect(element, null, element.key);
                              }
                            },
                          })}
                      className={`
                        ${buttonClassName}
                        flex
                        justify-start
                        items-center
                        rounded-lg
                        px-3 py-2
                        m-2
                        text-black
                        dark:text-white
                        hover:bg-primary
                        dark:hover:text-black
                        whitespace-nowrap
                        overflow-ellipsis
                        overflow-hidden
                        cursor-pointer
                        select-none
                        group
                      `}
                    >
                      {element}
                    </div>
                    {withBorder && (
                      <div className="h-px w-full bg-grey-10 dark:bg-grey-80"></div>
                    )}
                  </React.Fragment>
                );
              })}
          {tooltipChildren.element &&
            tooltipChildren.placement === 'bottom' &&
            tooltipChildren.element}
        </div>
      </TooltipPortal>
    </div>
  );
};
